import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Snowflake, Sun, Check, Phone, Mail, MapPin, Clock, Heart, Zap, Shield, Star, Flame, Droplets, ThermometerSun, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONTACT, CONTACT_GREENVILLE } from '../data/constants';
import SocialPostBanner from '../components/SocialPostBanner';

const MembershipPage = () => {
  const [selectedLocation, setSelectedLocation] = useState('naples');
  
  // Fire & Ice Society logo URL from the uploaded image
  const fireIceLogo = 'https://customer-assets.emergentagent.com/job_621329f7-e9c7-4cd9-a051-fb07c45c27cb/artifacts/fnlnkvan_jjdi8po4_unnamed.png';
  
  const coldPlungePackages = [
    { sessions: 'Drop In', price: '$29.95', priceValue: 29.95, savings: null },
    { sessions: '5 Sessions', price: '$89.95', priceValue: 89.95, savings: 'Save $59.80' },
    { sessions: '10 Sessions', price: '$119.95', priceValue: 119.95, savings: 'Save $179.55' },
    { sessions: '20 Sessions', price: '$219.95', priceValue: 219.95, savings: 'Save $379.05' },
    { sessions: '30 Sessions', price: '$299.95', priceValue: 299.95, savings: 'Save $598.55' },
  ];
  
  const redLightPackages = [
    { sessions: 'Drop In', price: '$34.95', priceValue: 34.95, savings: null },
    { sessions: '5 Sessions', price: '$99.95', priceValue: 99.95, savings: 'Save $74.80' },
    { sessions: '10 Sessions', price: '$174.95', priceValue: 174.95, savings: 'Save $174.55' },
    { sessions: '20 Sessions', price: '$220.95', priceValue: 220.95, savings: 'Save $478.05' },
    { sessions: '30 Sessions', price: '$299.95', priceValue: 299.95, savings: 'Save $748.55' },
  ];

  const infraredSaunaPackages = [
    { sessions: 'Drop In', price: '$39.95', priceValue: 39.95, savings: null },
    { sessions: '5 Sessions', price: '$149.95', priceValue: 149.95, savings: 'Save $49.80' },
    { sessions: '10 Sessions', price: '$279.95', priceValue: 279.95, savings: 'Save $119.55' },
    { sessions: '20 Sessions', price: '$499.95', priceValue: 499.95, savings: 'Save $299.05' },
    { sessions: '30 Sessions', price: '$699.95', priceValue: 699.95, savings: 'Save $498.55' },
  ];
  
  const coldPlungeBenefits = [
    'Reduce Inflammation',
    'Muscle Recovery',
    'Improve Circulation',
    'Sharpen Focus',
    'Strengthen Immune Response',
    'Boost Sleep Quality',
    'Releases Endorphins',
    'Manage Pain',
  ];
  
  const redLightBenefits = [
    'Enhance cellular regeneration',
    'Reduce inflammation',
    'Improve skin health & tone',
    'Accelerate wound healing',
    'Reduce joint pain & stiffness',
    'Boost collagen production',
  ];

  const infraredSaunaBenefits = [
    'Detox + Cleanse',
    'Improves Skin',
    'Muscle Recovery',
    'Gain Longevity',
    'Boosts Mood',
    'Improves Heart Health',
    'Boosts Metabolism',
    'Manages Pain',
  ];

  const contrastTherapySteps = [
    {
      step: 1,
      title: 'Start in the Sauna',
      description: 'Spend 20-30 minutes letting the red light & infrared heat deeply detoxify and prepare your body.',
      icon: Flame,
      color: 'from-orange-500 to-red-500'
    },
    {
      step: 2,
      title: 'Dive into the Cold Plunge',
      description: 'Spend 2-5 minutes in the cold plunge to complete the contrast therapy cycle.',
      icon: Snowflake,
      color: 'from-cyan-500 to-blue-500'
    },
    {
      step: 3,
      title: 'Repeat',
      description: 'For maximum benefits, alternate between the sauna and cold plunge 2-3 times during your session.',
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Membership | Fire & Ice Society | Contrast Therapy, Cold Plunge & Red Light | Leisure Wellness Naples</title>
        <meta name="description" content="Experience contrast therapy at Fire & Ice Society. Infrared sauna, cold plunge therapy and red light therapy in Naples FL and Greenville. Session packages available." />
        <meta name="keywords" content="contrast therapy, infrared sauna, cold plunge therapy, red light therapy, cryotherapy, wellness membership, Naples FL, Greenville, Fire Ice Society, recovery therapy" />
        <meta property="og:title" content="Fire & Ice Society Membership | Leisure Wellness Naples" />
        <meta property="og:description" content="Contrast therapy with infrared sauna, cold plunge and red light therapy sessions. Join the Fire & Ice Society today!" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.leisurewellnessnaples.com/membership" />
      </Helmet>
      
      <div className="pt-32 pb-20 min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 50%, #0a1628 100%)' }} data-testid="membership-page">
        {/* Hero Section with Fire & Ice Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-4 md:px-8 text-center mb-12"
        >
          <img 
            src={fireIceLogo} 
            alt="Fire & Ice Society" 
            className="h-48 md:h-64 mx-auto mb-8 object-contain"
          />
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold uppercase text-white mb-4">
            Contrast Therapy
          </h1>
          <p className="text-2xl md:text-3xl font-light text-slate-300 mb-4">
            One Perfect Balance
          </p>
          <p className="text-xl text-orange-400 font-semibold mb-6">
            One Suite. Two Temperatures. Three Experiences.
          </p>
          <p className="text-lg text-slate-300 max-w-4xl mx-auto">
            We've crafted a unique recovery journey that is a scientifically-backed approach to self-care, 
            combining hot and cold temperatures for optimal mental and physical performance and wellness.
            Each private suite is a luxurious haven where you can personalize your wellness routine in complete privacy.
          </p>
          
          {/* Location Toggle */}
          <div className="mt-8 flex justify-center gap-4">
            <button
              onClick={() => setSelectedLocation('naples')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedLocation === 'naples'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <MapPin className="inline-block mr-2" size={18} />
              Naples, FL
            </button>
            <button
              onClick={() => setSelectedLocation('greenville')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedLocation === 'greenville'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <MapPin className="inline-block mr-2" size={18} />
              Greenville
            </button>
          </div>
        </motion.div>

        {/* Hot Meets Cold Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-7xl mx-auto px-4 md:px-8 mb-12"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-purple-600 to-cyan-600 p-1">
            <div className="bg-slate-900/95 rounded-xl p-8 text-center">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-white mb-2">
                Hot <span className="italic text-orange-400">Meets</span> Cold
              </h2>
              <p className="text-xl text-slate-300">
                Infrared Sauna • Cold Plunge • Red Light Therapy
              </p>
            </div>
          </div>
        </motion.div>

        {/* Matched brand graphics */}
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SocialPostBanner ids={['lion-penguin-membership', 'dolphin-pricing']} />
        </div>

        {/* Services Grid - 3 Columns */}
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            
            {/* Infrared Sauna Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-orange-900/50 to-red-900/50 backdrop-blur-sm rounded-2xl border border-orange-500/30 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-600 to-red-600 p-5 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <ThermometerSun size={28} className="text-orange-200" />
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase">
                    Infrared Sauna
                  </h2>
                </div>
                <p className="text-orange-100 text-sm">
                  Heat for Relaxation
                </p>
              </div>
              
              {/* Description */}
              <div className="p-5 border-b border-orange-500/20">
                <p className="text-slate-300 text-sm mb-3">
                  Step into our advanced infrared sauna, designed for deep detoxification and muscle recovery. 
                  Unlike traditional saunas that heat the air around you, our infrared saunas heat your body directly and more effectively.
                </p>
                
                {/* Specs */}
                <div className="grid grid-cols-1 gap-2 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-orange-300">
                    <Flame size={14} />
                    <span><strong>Temperature:</strong> up to 170°F</span>
                  </div>
                  <div className="flex items-center gap-2 text-orange-300">
                    <Clock size={14} />
                    <span><strong>Duration:</strong> Up to 45 mins per session</span>
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="grid grid-cols-2 gap-1.5">
                  {infraredSaunaBenefits.map((benefit, idx) => (
                    <div key={`ir-benefit-${benefit.slice(0, 15).replace(/\s/g, '-')}`} className="flex items-center gap-1.5 text-xs text-orange-200">
                      <Check size={12} className="text-orange-400 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pricing Packages */}
              <div className="p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Star size={14} className="text-orange-400" />
                  Session Packages
                </h3>
                <div className="space-y-2">
                  {infraredSaunaPackages.map((pkg, idx) => (
                    <div
                      key={`ir-pkg-${pkg.sessions}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 
                          ? 'bg-white/5 border border-white/10' 
                          : 'bg-orange-500/10 border border-orange-500/20'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-white text-sm">{pkg.sessions}</span>
                        {pkg.savings && (
                          <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">
                            {pkg.savings}
                          </span>
                        )}
                      </div>
                      <span className="text-xl font-bold text-orange-300">{pkg.price}</span>
                    </div>
                  ))}
                </div>
                
                {/* CTA Button */}
                <Link
                  to="/contact"
                  className="mt-4 block w-full text-center bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 rounded-lg hover:from-orange-400 hover:to-red-400 transition-all shadow-lg text-sm"
                >
                  Purchase Sauna Package
                </Link>
              </div>
            </motion.div>

            {/* Cold Plunge Therapy Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-cyan-900/50 to-blue-900/50 backdrop-blur-sm rounded-2xl border border-cyan-500/30 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-5 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Snowflake size={28} className="text-cyan-200" />
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase">
                    Cold Plunge
                  </h2>
                </div>
                <p className="text-cyan-100 text-sm">
                  Chill for Clarity
                </p>
              </div>
              
              {/* Description */}
              <div className="p-5 border-b border-cyan-500/20">
                <p className="text-slate-300 text-sm mb-3">
                  Take the plunge into water chilled to 48-55°F and discover the power of deliberate cold exposure. 
                  Our premium cold plunge invigorates your body, sharpens your focus, and accelerates your recovery.
                </p>
                
                {/* Specs */}
                <div className="grid grid-cols-1 gap-2 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-cyan-300">
                    <Snowflake size={14} />
                    <span><strong>Temperature:</strong> 48-55°F</span>
                  </div>
                  <div className="flex items-center gap-2 text-cyan-300">
                    <Clock size={14} />
                    <span><strong>Duration:</strong> 2-5 mins per session</span>
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="grid grid-cols-2 gap-1.5">
                  {coldPlungeBenefits.map((benefit, idx) => (
                    <div key={`cp-benefit-${benefit.slice(0, 15).replace(/\s/g, '-')}`} className="flex items-center gap-1.5 text-xs text-cyan-200">
                      <Check size={12} className="text-cyan-400 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pricing Packages */}
              <div className="p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Star size={14} className="text-cyan-400" />
                  Session Packages
                </h3>
                <div className="space-y-2">
                  {coldPlungePackages.map((pkg, idx) => (
                    <div
                      key={`cp-pkg-${pkg.sessions}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 
                          ? 'bg-white/5 border border-white/10' 
                          : 'bg-cyan-500/10 border border-cyan-500/20'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-white text-sm">{pkg.sessions}</span>
                        {pkg.savings && (
                          <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">
                            {pkg.savings}
                          </span>
                        )}
                      </div>
                      <span className="text-xl font-bold text-cyan-300">{pkg.price}</span>
                    </div>
                  ))}
                </div>
                
                {/* CTA Button */}
                <Link
                  to="/contact"
                  className="mt-4 block w-full text-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-3 rounded-lg hover:from-cyan-400 hover:to-blue-400 transition-all shadow-lg text-sm"
                >
                  Purchase Cold Plunge Package
                </Link>
              </div>
            </motion.div>
            
            {/* Red Light Therapy Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-red-900/50 to-pink-900/50 backdrop-blur-sm rounded-2xl border border-red-500/30 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-pink-600 p-5 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Sun size={28} className="text-red-200" />
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase">
                    Red Light Therapy
                  </h2>
                </div>
                <p className="text-red-100 text-sm">
                  Glow for Recovery
                </p>
              </div>
              
              {/* Description */}
              <div className="p-5 border-b border-red-500/20">
                <p className="text-slate-300 text-sm mb-3">
                  Enhance recovery and skin health with red light therapy. Boosts cellular regeneration, 
                  reduces inflammation, and improves overall wellness through photobiomodulation.
                </p>
                
                {/* Specs */}
                <div className="grid grid-cols-1 gap-2 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-red-300">
                    <Sun size={14} />
                    <span><strong>Wavelength:</strong> 630-850nm</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-300">
                    <Clock size={14} />
                    <span><strong>Duration:</strong> 10-20 mins per session</span>
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="grid grid-cols-2 gap-1.5">
                  {redLightBenefits.map((benefit, idx) => (
                    <div key={`rl-benefit-${benefit.slice(0, 15).replace(/\s/g, '-')}`} className="flex items-center gap-1.5 text-xs text-red-200">
                      <Check size={12} className="text-red-400 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pricing Packages */}
              <div className="p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Star size={14} className="text-red-400" />
                  Session Packages
                </h3>
                <div className="space-y-2">
                  {redLightPackages.map((pkg, idx) => (
                    <div
                      key={`rl-pkg-${pkg.sessions}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 
                          ? 'bg-white/5 border border-white/10' 
                          : 'bg-red-500/10 border border-red-500/20'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-white text-sm">{pkg.sessions}</span>
                        {pkg.savings && (
                          <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">
                            {pkg.savings}
                          </span>
                        )}
                      </div>
                      <span className="text-xl font-bold text-red-300">{pkg.price}</span>
                    </div>
                  ))}
                </div>
                
                {/* CTA Button */}
                <Link
                  to="/contact"
                  className="mt-4 block w-full text-center bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold py-3 rounded-lg hover:from-red-400 hover:to-pink-400 transition-all shadow-lg text-sm"
                >
                  Purchase Red Light Package
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Enhance Your Experience Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="text-center mb-10">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase text-white mb-4">
                Enhance Your Experience
              </h2>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                Our private suites feature customizable lighting, sound, and entertainment options to help you 
                create the perfect ambiance. Stream your favorite shows, listen to relaxing music, or simply enjoy the tranquility.
              </p>
            </div>

            {/* Contrast Therapy Steps */}
            <div className="grid sm:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
              {contrastTherapySteps.map((step, idx) => (
                <motion.div
                  key={`contrast-step-${step.step}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center"
                >
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-r from-orange-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {step.step}
                  </div>
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center`}>
                    <step.icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Why Join Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase text-white mb-8">
              Why Choose Fire & Ice Society?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Heart,
                  title: 'Holistic Wellness',
                  description: 'Combine hot and cold therapies for complete body recovery and wellness.',
                  color: 'from-pink-500 to-red-500'
                },
                {
                  icon: Zap,
                  title: 'Fast Results',
                  description: 'Feel the difference after your first session. Energy, clarity, and recovery.',
                  color: 'from-yellow-500 to-orange-500'
                },
                {
                  icon: Shield,
                  title: 'Private Suites',
                  description: 'Luxurious private suites with customizable lighting, sound, and entertainment.',
                  color: 'from-cyan-500 to-blue-500'
                }
              ].map((item, idx) => (
                <div
                  key={`why-${item.title}`}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center"
                >
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center`}>
                    <item.icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Experience Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="relative overflow-hidden rounded-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600/80 via-purple-600/80 to-cyan-600/80"></div>
              <div className="relative p-8 md:p-12 text-center">
                <h2 className="font-['Barlow_Condensed'] text-2xl md:text-4xl font-bold uppercase text-white mb-4">
                  Experience the Perfect Balance of Hot and Cold
                </h2>
                <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
                  This isn't just about recovery—it's an immersive experience designed for both relaxation and rejuvenation.
                </p>
                <Link
                  to="/contact"
                  className="inline-block bg-white text-slate-900 font-bold px-8 py-4 rounded-lg hover:bg-slate-100 transition-all shadow-lg"
                >
                  Schedule Your First Session
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center"
          >
            <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-white mb-4">
              Ready to Start Your Wellness Journey?
            </h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Contact us to purchase your membership package or book your first session. 
              Our team is here to help you achieve your wellness goals.
            </p>
            
            {/* Location Hours Display */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 max-w-md mx-auto">
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center justify-center gap-2">
                <Clock size={20} className={selectedLocation === 'naples' ? 'text-orange-400' : 'text-cyan-400'} />
                {selectedLocation === 'naples' ? 'Naples, FL' : 'Greenville, SC'} Hours
              </h3>
              <div className="space-y-2">
                {(selectedLocation === 'naples' ? CONTACT.detailedHours : CONTACT_GREENVILLE.detailedHours).map((item, idx) => (
                  <div key={`hours-${item.day}`} className="flex justify-between text-slate-300">
                    <span className="font-medium">{item.day}</span>
                    <span>{item.hours}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <a
                href={`tel:${(selectedLocation === 'naples' ? CONTACT : CONTACT_GREENVILLE).phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-all"
              >
                <Phone size={18} />
                {selectedLocation === 'naples' ? CONTACT.phone : CONTACT_GREENVILLE.phone}
              </a>
              <a
                href={`mailto:${selectedLocation === 'naples' ? CONTACT.email : CONTACT_GREENVILLE.email}`}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-all"
              >
                <Mail size={18} />
                {selectedLocation === 'naples' ? CONTACT.email : CONTACT_GREENVILLE.email}
              </a>
            </div>
            <Link
              to="/contact"
              className="inline-block bg-gradient-to-r from-orange-500 via-red-500 to-cyan-500 text-white font-bold px-8 py-4 rounded-lg hover:opacity-90 transition-all shadow-lg"
            >
              Contact Us to Purchase
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MembershipPage;
